import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Download, X, CheckCircle2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ProposalView from "./ProposalView";
import { exportNodeToPdfPaged } from "../lib/pdf";
import { fmtCurrency } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_CONTACT = { name: "", email: "", phone: "" };

export default function ProposalPublic() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selection, setSelection] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company_name: "", start_date: "",
    main_contact: { ...EMPTY_CONTACT }, billing_contact: { ...EMPTY_CONTACT }, billing_same: false,
  });
  const viewRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/proposals/${token}`)
      .then(({ data }) => {
        setProposal(data);
        axios.post(`${BACKEND_URL}/api/public/proposals/${token}/event`, { type: "open" }, { headers: { "X-Requested-With": "XMLHttpRequest" } }).catch(() => {});
      })
      .catch(() => setError("This proposal is not available. It may have been unpublished or the link is incorrect."));
  }, [token]);

  const seenOffices = useRef(new Set());
  const trackOffice = (officeNumber) => {
    if (seenOffices.current.has(officeNumber)) return;
    seenOffices.current.add(officeNumber);
    axios.post(`${BACKEND_URL}/api/public/proposals/${token}/event`, { type: "office_view", office_number: officeNumber }, { headers: { "X-Requested-With": "XMLHttpRequest" } }).catch(() => {});
  };

  const toggleTerm = (o, t) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (next[o.office_number]?.term === t.key) {
        delete next[o.office_number];
      } else {
        next[o.office_number] = {
          office_number: o.office_number, location: o.location || "",
          term: t.key, term_label: t.label, price: t.value, desks: o.desks ?? null,
        };
      }
      return next;
    });
  };

  const selected = Object.values(selection);
  const total = selected.reduce((s, x) => s + (x.price || 0), 0);

  const download = async () => {
    setDownloading(true);
    try {
      await exportNodeToPdfPaged(viewRef.current, `${(proposal.title || "proposal").replace(/[^a-z0-9]+/gi, "-")}.pdf`);
    } catch { toast.error("Could not generate the PDF."); }
    finally { setDownloading(false); }
  };

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required."); return; }
    if (!form.main_contact.name.trim() || !form.main_contact.email.trim()) {
      toast.error("Main contact name and email are required."); return;
    }
    const billing = form.billing_same ? { ...form.main_contact } : form.billing_contact;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/proposals/${token}/request`, {
        offices: selected, company_name: form.company_name.trim(),
        main_contact: form.main_contact, billing_contact: billing, start_date: form.start_date,
      }, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      setDialogOpen(false);
      setDone(true);
      setSelection({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your request. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-brand-page flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-serif text-3xl text-brand-ink">Proposal unavailable</p>
          <p className="text-sm text-brand-muted mt-3 max-w-md">{error}</p>
        </div>
      </div>
    );
  }
  if (!proposal) {
    return <div className="min-h-screen bg-brand-page flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>;
  }

  const setMain = (k, v) => setForm((f) => ({ ...f, main_contact: { ...f.main_contact, [k]: v } }));
  const setBill = (k, v) => setForm((f) => ({ ...f, billing_contact: { ...f.billing_contact, [k]: v } }));

  return (
    <div className="min-h-screen bg-brand-page proposal-theme">
      <div className="sticky top-0 z-20 bg-brand-page/90 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-[880px] mx-auto px-8 sm:px-14 h-14 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">Workspace Proposal</span>
          <button onClick={download} disabled={downloading} data-testid="public-download-pdf"
            className="inline-flex items-center gap-2 text-sm bg-brand-ink text-brand-page px-4 py-2 hover:bg-brand-primary hover:text-brand-page active:scale-[0.98] transition-all disabled:opacity-60">
            <Download className="h-4 w-4" /> {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      <ProposalView ref={viewRef} proposal={proposal} onOfficeSeen={trackOffice} selection={selection} onToggleTerm={toggleTerm} />

      {/* Floating request bar */}
      {selected.length > 0 && !dialogOpen && (
        <div className="fixed bottom-0 inset-x-0 z-30" data-testid="proposal-request-bar">
          <div className="max-w-[880px] mx-auto px-4 pb-4">
            <div className="rounded-xl border border-brand-gold bg-brand-ink text-brand-page shadow-[0_10px_40px_rgba(0,0,0,0.35)] px-5 py-3.5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">{selected.length} office{selected.length === 1 ? "" : "s"} selected · <span className="font-mono">{fmtCurrency(total)}/mo</span></p>
                <p className="text-[11px] text-brand-page/60 truncate">Requesting an agreement — nothing is finalized until it's signed</p>
              </div>
              <button onClick={() => setDialogOpen(true)} data-testid="open-request-dialog"
                className="shrink-0 inline-flex items-center gap-2 bg-brand-gold text-brand-ink px-4 py-2 rounded-md text-sm font-semibold hover:brightness-105 active:scale-[0.98] transition-all">
                Request agreement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success state */}
      {done && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center px-4" data-testid="request-success">
          <div className="bg-brand-page rounded-xl max-w-md w-full p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-brand-gold mx-auto" />
            <p className="font-serif text-2xl text-brand-ink mt-4">Request received</p>
            <p className="text-sm text-brand-muted mt-2">
              Thanks! Our team has been notified and will prepare an agreement for your selected office(s). Remember — nothing is finalized until the agreement is signed.
            </p>
            <button onClick={() => setDone(false)} data-testid="request-success-close"
              className="mt-6 inline-flex items-center justify-center bg-brand-ink text-brand-page px-5 py-2.5 rounded-md text-sm hover:bg-brand-primary transition-colors">
              Back to proposal
            </button>
          </div>
        </div>
      )}

      {/* Request dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center sm:px-4 overflow-y-auto" data-testid="request-dialog">
          <div className="bg-brand-page w-full sm:max-w-lg sm:rounded-xl shadow-2xl my-0 sm:my-8 max-h-screen overflow-y-auto">
            <div className="sticky top-0 bg-brand-page border-b border-brand-border px-6 py-4 flex items-center justify-between">
              <p className="font-serif text-xl text-brand-ink">Request an agreement</p>
              <button onClick={() => setDialogOpen(false)} data-testid="close-request-dialog" className="text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Confirmation summary */}
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">You're requesting</p>
                <div className="rounded-lg border border-brand-border divide-y divide-brand-border" data-testid="request-summary">
                  {selected.map((s) => (
                    <div key={s.office_number} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm text-brand-ink font-medium">Office {s.office_number}{s.location ? ` · ${s.location}` : ""}</p>
                        <p className="text-[12px] text-brand-muted">{s.term_label}</p>
                      </div>
                      <span className="font-mono text-sm text-brand-ink whitespace-nowrap">{s.price != null ? `${fmtCurrency(s.price)}/mo` : "—"}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-brand-gold/10">
                    <span className="text-[12px] uppercase tracking-wide text-brand-ink font-medium">Total</span>
                    <span className="font-mono text-base text-brand-ink font-semibold">{fmtCurrency(total)}/mo</span>
                  </div>
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[12px] text-brand-muted">
                  <ShieldCheck className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
                  This only requests an agreement for the option(s) above. Pricing shown is per month for the selected term. Nothing is finalized until an agreement is signed.
                </p>
              </div>

              {/* Contract verification */}
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-[0.15em] text-brand-tertiary">A few details for the agreement</p>
                <Field label="Company name" required value={form.company_name} onChange={(v) => setForm((f) => ({ ...f, company_name: v }))} testid="req-company" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Preferred start date" type="date" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} testid="req-start" />
                </div>

                <p className="text-[11px] uppercase tracking-[0.15em] text-brand-tertiary pt-1">Main contact</p>
                <Field label="Full name" required value={form.main_contact.name} onChange={(v) => setMain("name", v)} testid="req-main-name" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Email" required type="email" value={form.main_contact.email} onChange={(v) => setMain("email", v)} testid="req-main-email" />
                  <Field label="Phone" type="tel" value={form.main_contact.phone} onChange={(v) => setMain("phone", v)} testid="req-main-phone" />
                </div>

                <div className="flex items-center justify-between pt-1">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-brand-tertiary">Billing contact</p>
                  <label className="flex items-center gap-2 text-[12px] text-brand-muted cursor-pointer">
                    <input type="checkbox" checked={form.billing_same} data-testid="req-billing-same"
                      onChange={(e) => setForm((f) => ({ ...f, billing_same: e.target.checked }))}
                      className="h-3.5 w-3.5 accent-brand-gold" />
                    Same as main contact
                  </label>
                </div>
                {!form.billing_same && (
                  <>
                    <Field label="Full name" value={form.billing_contact.name} onChange={(v) => setBill("name", v)} testid="req-bill-name" />
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Email" type="email" value={form.billing_contact.email} onChange={(v) => setBill("email", v)} testid="req-bill-email" />
                      <Field label="Phone" type="tel" value={form.billing_contact.phone} onChange={(v) => setBill("phone", v)} testid="req-bill-phone" />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-brand-page border-t border-brand-border px-6 py-4 flex items-center justify-end gap-3">
              <button onClick={() => setDialogOpen(false)} className="text-sm text-brand-muted hover:text-brand-ink px-4 py-2">Cancel</button>
              <button onClick={submit} disabled={submitting} data-testid="submit-request"
                className="inline-flex items-center gap-2 bg-brand-gold text-brand-ink px-5 py-2.5 rounded-md text-sm font-semibold hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-60">
                {submitting ? "Sending…" : "Request agreement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] text-brand-muted">{label}{required && <span className="text-brand-gold"> *</span>}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full rounded-md border border-brand-border bg-brand-page px-3 py-2 text-sm text-brand-ink outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold" />
    </label>
  );
}
